var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Log } from '../../../../log';
import { DriverAdapter } from './DriverAdapter';
import { DriverLogChannel } from './DriverLog';
import { DriverState } from './DriverState';
import { StateBridge } from './StateBridge';
import { UdpCommandSocket } from './UdpCommandSocket';
export class UdpDriverAdapter extends DriverAdapter {
    constructor(equipmentId, driverName, Driver, ipAddress, simulate, onConnectionLost, onControlStatus, translate, logFactory) {
        super(Driver.key, Driver.emulatorKey, ipAddress, simulate, logFactory, translate);
        this.equipmentId = equipmentId;
        this.driverName = driverName;
        this.Driver = Driver;
        this.onConnectionLost = onConnectionLost;
        this.onControlStatus = onControlStatus;
        this.isConnecting = false;
        this.isDisconnectPending = false;
        this.onSocketConnectionLost = this.onSocketConnectionLost.bind(this);
    }
    get transport() {
        if (this.socket === undefined)
            throw new Error(`socket is undefined`);
        return this.socket;
    }
    computeInitialOfflineCaps(config) {
        var _a, _b;
        const socketOptionsArray = typeof this.Driver.socketOptions === 'function'
            ? this.Driver.socketOptions(config)
            : this.Driver.socketOptions;
        const socketOptions = socketOptionsArray[0];
        const emulatorKey = (_a = this.Driver.emulatorKey) !== null && _a !== void 0 ? _a : this.Driver.key;
        const socket = new UdpCommandSocket(this.logFactory, socketOptions, this.simulate ? emulatorKey : undefined);
        this.state = new DriverState(this.logFactory, {});
        this.driver = new this.Driver(socket, this.state, new DriverLogChannel(Log.channel(this.driverName), this.ipAddress), this);
        (_b = this.stateBridge) === null || _b === void 0 ? void 0 : _b.destroy();
        this.stateBridge = new StateBridge(this.state, this.equipmentId, this.driver, socket, this.onControlStatus, this, this.logFactory);
        super.computeInitialOfflineCaps(config);
        socket.close();
    }
    doConfigure(config, token) {
        const _super = Object.create(null, {
            doConfigure: { get: () => super.doConfigure }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return;
            if (this.socket === undefined) {
                this.log.error(`socket is undefined`);
                return;
            }
            if (this.socket.emulator !== undefined && this.socket.emulator.onConfigure !== undefined) {
                this.socket.emulator.onConfigure(config);
            }
            return _super.doConfigure.call(this, config, token);
        });
    }
    doConnect(credentials, config, storage, token) {
        const _super = Object.create(null, {
            doConnect: { get: () => super.doConnect }
        });
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return 'connectFailed';
            let result = yield _super.doConnect.call(this, credentials, config, storage, token);
            if (result !== 'connected')
                return result;
            if (token.isCancelled)
                return 'connectFailed';
            const emulatorKey = (_a = this.Driver.emulatorKey) !== null && _a !== void 0 ? _a : this.Driver.key;
            const socketOptionsArray = typeof this.Driver.socketOptions === 'function'
                ? this.Driver.socketOptions(config)
                : this.Driver.socketOptions;
            this.isConnecting = true;
            for (const socketOptions of socketOptionsArray) {
                const addr = socketOptions.port
                    ? `${this.ipAddress}:${socketOptions.port}`
                    : this.ipAddress;
                const sim = this.simulate ? ` (simulated)` : ``;
                this.log.info(`trying connection to ${addr}${sim}`);
                try {
                    this.socket = new UdpCommandSocket(this.logFactory, socketOptions, this.simulate ? emulatorKey : undefined);
                    if (this.simulate && ((_b = this.socket.emulator) === null || _b === void 0 ? void 0 : _b.onConfigure)) {
                        this.socket.emulator.onConfigure(config);
                    }
                    this.socket.onConnectionLost(this.onSocketConnectionLost);
                    yield this.socket.bind();
                    yield this.socket.connect(this.ipAddress, credentials.username, credentials.password);
                }
                catch (error) {
                    this.log.info(`failed connecting: ${error.message}`);
                    this.socket = undefined;
                    continue;
                }
                this.state = new DriverState(this.logFactory, storage);
                this.driver = new this.Driver(this.socket, this.state, new DriverLogChannel(Log.channel(this.driverName), this.ipAddress), this);
                (_c = this.stateBridge) === null || _c === void 0 ? void 0 : _c.destroy();
                this.stateBridge = new StateBridge(this.state, this.equipmentId, this.driver, this.socket, this.onControlStatus, this, this.logFactory);
                result = yield this.doInitialize(credentials, config, storage, token);
                if (token.isCancelled)
                    return 'connectFailed';
                if (this.isDisconnectPending) {
                    this.isConnecting = false;
                    yield this.doDisconnect(true, token);
                    return 'connectFailed';
                }
                if (result !== 'connected') {
                    if (!this.socket.isClosed)
                        this.socket.close();
                    this.socket = undefined;
                }
                if (result === 'connected')
                    this.log.info(`connected to ${addr}${sim}`);
                if (result !== 'connectFailed')
                    break;
            }
            this.isConnecting = false;
            return result;
        });
    }
    doDisconnect(expected, token) {
        const _super = Object.create(null, {
            doDisconnect: { get: () => super.doDisconnect }
        });
        return __awaiter(this, void 0, void 0, function* () {
            if (token.isCancelled)
                return;
            if (this.isConnecting) {
                this.isDisconnectPending = true;
            }
            else if (this.socket !== undefined) {
                this.isDisconnectPending = false;
                yield _super.doDisconnect.call(this, true, token);
                if (token.isCancelled)
                    return;
                if (this.socket.emulator !== undefined &&
                    this.socket.emulator.onDisconnect !== undefined) {
                    this.socket.emulator.onDisconnect();
                }
                if (!this.socket.isClosed)
                    this.socket.close();
                this.socket = undefined;
                this.phase = 'disconnected';
            }
        });
    }
    onSocketConnectionLost() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.stateBridge === undefined)
                throw new Error(`stateBridge undefined`);
            this.stateBridge.isConnected = false;
            yield this.disconnect(false);
            this.phase = 'disconnected';
            this.isConnecting = false;
            this.isDisconnectPending = false;
            if (this.socket !== undefined && !this.socket.isClosed)
                this.socket.close();
            this.socket = undefined;
            this.onConnectionLost();
        });
    }
    resume() {
        var _a;
        (_a = this.socket) === null || _a === void 0 ? void 0 : _a.resume();
        super.resume();
    }
    suspend(delayMs, suspendType = 'all') {
        var _a;
        (_a = this.socket) === null || _a === void 0 ? void 0 : _a.suspend(delayMs === undefined ? undefined : delayMs + 100, suspendType === 'all' ? 'all' : 'idle');
        super.suspend(delayMs, suspendType);
    }
}
